import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

import { TopBar, Btn, IconButton, Avatar, AtomBubble } from '../components/wr';
import { useWebSocket } from '../context/GameContext';
import { useAuth } from '../context/AuthContext';
import { fetchSoloHistory, type SoloGameRecord } from '../services/soloHistoryService';

type Difficulty = 'easy' | 'medium' | 'hard' | 'expert' | 'insane';

const DIFFICULTY_LEVELS: Array<{
  value: Difficulty;
  label: string;
  hint: string;
  /** Who Atom becomes at this level. Must match `PERSONA_BY_DIFFICULTY`
   *  (backend/src/services/atomVoice.js) — the server picks the same voice. */
  persona: string;
  /** Words Atom is actually drawing from at this level. Real ones, from the
   *  pool it really plays out of. */
  words: string;
  /** Atom's vibe at this level — shows in the AtomBubble at the bottom, IN the
   *  voice it will use all game. Pick Easy and you are told, by the Coach, that
   *  you are getting the Coach. */
  atomLine: string;
}> = [
  {
    value: 'easy', label: 'Easy', persona: 'The Coach',
    hint: 'Everyday words — and it will keep writing the same ones as you',
    words: 'Cat · Dog · Ball · Canada',
    atomLine: "Easy. I'll write the first word that comes to me — same as you, probably.",
  },
  {
    value: 'medium', label: 'Medium', persona: 'The Neighbour',
    hint: 'Solid, everyday answers. A fair game.',
    words: 'Zebra · Rabbit · Piano · Denmark',
    atomLine: "Medium. I'll keep up, but this one's yours to win.",
  },
  {
    value: 'hard', label: 'Hard', persona: 'The Wordsmith',
    hint: "Words you know but wouldn't have reached for",
    words: 'Otter · Iguana · Compass · Colombo',
    atomLine: "Hard. I'm going for the words you didn't think of.",
  },
  {
    value: 'expert', label: 'Expert', persona: 'The Rival',
    hint: 'A well-read opponent. It will rarely duplicate you.',
    words: 'Ocelot · Sextant · Narwhal',
    atomLine: 'Expert. I intend to win this one.',
  },
  {
    value: 'insane', label: 'Insane', persona: 'The Rival',
    hint: 'Fast, and it never writes the same word as you. Good luck.',
    words: 'Cassowary · Xerus · Quetzal',
    atomLine: "Insane. I won't duplicate you, and I won't be slow. Ready?",
  },
];

const ROUND_OPTIONS: Array<3 | 5 | 7 | 10> = [3, 5, 7, 10];

/**
 * Solo.
 *
 * TWO DOORS, AND THEY ARE NOT THE SAME KIND OF THING.
 *
 *   THE LADDER  — a RUN. Atom escalates every round, you have one life, and your
 *                 depth is the result. You do NOT choose the difficulty, and that
 *                 is the entire feature: self-selected difficulty is self-selected
 *                 boredom, and the old solo screen was five options you shrugged
 *                 at and then picked the one you could beat. In a run those five
 *                 are a staircase you climb.
 *
 *   PRACTICE    — the solo game that already existed, unchanged. Pick an Atom,
 *                 pick a length, play a game. It is here because a run is a
 *                 commitment and sometimes you just want to warm up, and because
 *                 deleting a thing that works in order to make a point is not a
 *                 product decision.
 *
 * Solo auto-starts either way: no lobby, because there is nobody to wait for.
 */
export default function SoloSetupScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { createSoloGame, createLadderRun } = useWebSocket();
  const { currentUser, userProfile } = useAuth();

  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [rounds, setRounds] = useState<3 | 5 | 7 | 10>(5);
  const [creating, setCreating] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [history, setHistory] = useState<SoloGameRecord[]>([]);
  /** Practice is folded away by default — the Ladder is the headline. */
  const [practiceOpen, setPracticeOpen] = useState(false);

  /**
   * THE ONLY NUMBER THIS PRODUCT PERSISTS FOR SOLO.
   *
   * Read straight off the live user doc (AuthContext holds an onSnapshot on it),
   * server-written, and it only ever goes up. It is deliberately absent — not
   * zero, ABSENT — until you have finished a run: an empty state must be honest,
   * and "Best: 0" is a number about a thing that never happened.
   */
  const bestDepth = Number((userProfile as any)?.ladder_best_depth) || 0;

  // Pull the last 5 solo games so the user has context: "you're 3-0 vs.
  // medium — try hard?" Best-effort; quietly empty if not signed in.
  useEffect(() => {
    let cancelled = false;
    if (!currentUser) {
      setHistory([]);
      return;
    }
    fetchSoloHistory(currentUser.uid, 5).then(rows => {
      if (!cancelled) setHistory(rows);
    });
    return () => { cancelled = true; };
  }, [currentUser]);

  /**
   * START A RUN.
   *
   * No options, because there are none. The server picks the rung, the letter and
   * the Atom, and it starts you at Easy whether you like it or not.
   */
  const handleRun = async () => {
    if (creating) return;
    setCreating(true);
    setStatusMsg('Atom is warming up. It starts easy.');
    try {
      await createLadderRun();
      // App.tsx routes to the game on currentScreen='game'. Deliberately no
      // navigate() here — doing it ourselves would race the router.
    } catch (e: any) {
      console.error('Failed to start the run', e);
      const msg: string = e?.message || 'Something went wrong. Try again?';
      if (/auth/i.test(msg)) {
        setStatusMsg('Connection wobble — sign in again to continue.');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        setStatusMsg(msg);
      }
      setCreating(false);
    }
  };

  /** "Go again" from the run-over screen lands here and starts one immediately. */
  useEffect(() => {
    if ((location.state as any)?.startRun) {
      // Clear the flag first, or a back-navigation restarts the run under them.
      navigate(location.pathname, { replace: true, state: {} });
      void handleRun();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleStart = async () => {
    if (creating) return;
    setCreating(true);
    setStatusMsg('Atom is sharpening a pencil…');
    try {
      // One call: create the room (with rounds + difficulty, so the backend
      // seeds exactly one Atom at the right level), then START. GameContext
      // routes us straight to the wheel — no lobby, no second tap.
      await createSoloGame({ rounds, difficulty });
      // Navigation is handled by App.tsx reacting to currentScreen='game'.
      // Deliberately no navigate() here: doing it ourselves would race the
      // router and could unmount before startGame's ack lands.
    } catch (e: any) {
      console.error('Failed to start solo game', e);
      const msg: string = e?.message || 'Something went wrong. Try again?';
      // Auth race conditions surface here too — give the user a clear
      // out instead of a dead-end error.
      if (/auth/i.test(msg)) {
        setStatusMsg('Connection wobble — sign in again to continue.');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        setStatusMsg(msg);
      }
      setCreating(false);
    }
  };

  const current = DIFFICULTY_LEVELS.find(d => d.value === difficulty);
  const currentLine = current?.atomLine || '';

  return (
    <div className="app-shell">
      <div className="screen-pad">
        <TopBar
          left={<IconButton icon="ChevronLeft" ariaLabel="Back" onClick={() => navigate('/home')} />}
          center="Play"
        />

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="wr-col gap-4"
          style={{ flex: 1 }}
        >
          {/*
            THE LADDER. The headline, and the only thing on this screen with a
            number attached.

            One button, no options. What the card has to communicate in two
            seconds is: it escalates, you have one life, and there is a number
            that goes up. Everything else is the run's job to teach, and it will.
          */}
          <div
            className="card lg wr-col gap-3"
            style={{
              padding: 18,
              marginTop: 12,
              border: '2px solid var(--accent)',
              background: 'linear-gradient(160deg, var(--accent-soft), var(--surface) 70%)',
            }}
          >
            <div className="wr-row gap-3" style={{ alignItems: 'center' }}>
              <Avatar id="fox" size={52} />
              <div style={{ flex: 1 }}>
                <div className="eyebrow accent" style={{ fontSize: 11 }}>THE LADDER</div>
                <div className="h3" style={{ marginTop: 2 }}>One life. Atom gets harder.</div>
              </div>
              {/* HONEST EMPTY STATE. No "Best: 0" — a zero is a number about a
                  thing that never happened. Until you finish a run, there is
                  nothing here, and that is the truth. */}
              {bestDepth > 0 && (
                <div style={{ textAlign: 'right' }}>
                  <div className="eyebrow" style={{ fontSize: 10 }}>BEST</div>
                  <div
                    style={{
                      fontFamily: 'var(--display)',
                      fontWeight: 900,
                      fontSize: 30,
                      lineHeight: 1,
                      color: 'var(--accent)',
                    }}
                  >
                    {bestDepth}
                  </div>
                </div>
              )}
            </div>

            <div className="wr-text-3" style={{ fontSize: 13, lineHeight: 1.45 }}>
              Beat Atom in a round and you climb. Score less and the run is over.
              It starts easy, it is banning words by round 3, and round 5 is a Q, an
              X or a Z against an Atom that will not duplicate you.
            </div>

            <Btn
              kind="primary"
              size="xl"
              full
              icon="TrendingUp"
              iconRight="ArrowRight"
              loading={creating}
              onClick={handleRun}
            >
              {creating ? 'Starting…' : bestDepth > 0 ? `Beat round ${bestDepth}` : 'Start a run'}
            </Btn>
          </div>

          {/* ---- PRACTICE. The solo game that already existed. -------------- */}
          <button
            type="button"
            onClick={() => setPracticeOpen(o => !o)}
            className="wr-row gap-2"
            style={{
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 0',
              width: '100%',
            }}
          >
            <span className="eyebrow">Practice · pick your own Atom</span>
            <ChevronRight
              size={16}
              color="var(--ink-3)"
              style={{
                transform: practiceOpen ? 'rotate(90deg)' : 'none',
                transition: 'transform 140ms ease',
              }}
            />
          </button>

          {practiceOpen && (
            <>
          <div className="card lg" style={{ padding: 16 }}>
            <div className="wr-row gap-3" style={{ alignItems: 'center' }}>
              <Avatar id="fox" size={56} />
              <div style={{ flex: 1 }}>
                <div className="eyebrow accent" style={{ fontSize: 11 }}>YOUR OPPONENT</div>
                <div className="h3" style={{ marginTop: 2 }}>Atom · {current?.persona}</div>
                <div className="wr-text-3" style={{ fontSize: 12, marginTop: 2 }}>
                  Plays from {current?.words}
                </div>
              </div>
            </div>
          </div>

          {/* Difficulty selector. Five levels, and each one is a different SET
              OF WORDS Atom can reach — not a different chance of it fumbling.
              (It was the latter: `getBotErrorRate`, easy skipped 30% of its
              columns. Losing to an opponent that stopped rolling badly is not
              the same as losing to one that knew a word you didn't.)

              THE LADDER IS WHERE THESE FIVE MEAN SOMETHING. Here they are a menu,
              and a menu you pick your own difficulty from is a menu you pick the
              easy option from. That is fine for a warm-up and fatal for a game. */}
          <div className="wr-col gap-2">
            <div className="eyebrow">Difficulty</div>
            <div className="wr-col gap-2">
              {DIFFICULTY_LEVELS.map(d => {
                const active = d.value === difficulty;
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setDifficulty(d.value)}
                    aria-pressed={active}
                    className="prow"
                    style={{
                      cursor: 'pointer',
                      border: active ? '2px solid var(--accent)' : '1.5px solid var(--surface-2)',
                      background: active ? 'var(--accent-soft)' : 'var(--surface)',
                      transition: 'all 140ms ease',
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: active ? 'var(--accent)' : 'var(--surface-2)',
                        color: active ? '#fff' : 'var(--ink-2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'var(--display)',
                        fontWeight: 800,
                        fontSize: 14,
                      }}
                    >
                      {d.label[0]}
                    </div>
                    <div className="body">
                      <div className="name">{d.label}</div>
                      <div className="word" style={{ fontSize: 12 }}>{d.hint}</div>
                    </div>
                    {active && <ChevronRight size={18} color="var(--accent)" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rounds — segmented control. Pre-game state lives only on the
              client until createRoom; the backend then uses the value.
              (A run has no round count: it is as long as you survive.) */}
          <div className="wr-col gap-2">
            <div className="eyebrow">Rounds</div>
            <div className="segmented">
              {ROUND_OPTIONS.map(n => (
                <button
                  key={n}
                  className={`seg ${rounds === n ? 'active' : ''}`}
                  onClick={() => setRounds(n)}
                  type="button"
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <Btn
            kind="dark"
            size="lg"
            full
            icon="Play"
            loading={creating}
            onClick={handleStart}
          >
            {creating ? 'Starting…' : `Practice · ${rounds} rounds vs ${current?.label} Atom`}
          </Btn>
            </>
          )}

          {/* Recent solo games — chess.com-style record. Pulled from
              persisted game history once signed in. */}
          {currentUser && history.length > 0 && (
            <div className="wr-col gap-2">
              <div className="wr-row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div className="eyebrow">Recent solo</div>
                <button
                  type="button"
                  onClick={() => navigate('/history?filter=solo')}
                  style={{ fontSize: 12, color: 'var(--accent)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                >
                  View all →
                </button>
              </div>
              <div className="wr-col gap-1">
                {history.slice(0, 5).map(g => (
                  <button
                    key={g.gameId}
                    type="button"
                    onClick={() => navigate(`/games/${g.gameId}`)}
                    className="prow"
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      background: 'var(--surface)',
                      border: 'none',
                      width: '100%',
                    }}
                  >
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'var(--display)',
                        fontWeight: 800,
                        fontSize: 11,
                        color: '#fff',
                        background:
                          g.outcome === 'win'
                            ? 'var(--green, #1f8f4a)'
                            : g.outcome === 'loss'
                            ? 'var(--coral, #b94a3b)'
                            : 'var(--ink-3)',
                      }}
                    >
                      {g.outcome === 'win' ? 'W' : g.outcome === 'loss' ? 'L' : 'D'}
                    </span>
                    <div className="body">
                      <div className="name" style={{ fontSize: 13 }}>vs. Atom · {g.difficulty}</div>
                      <div className="word" style={{ fontSize: 11 }}>
                        {g.youScore}–{g.botScore} · {g.rounds} rounds
                      </div>
                    </div>
                    <ChevronRight size={14} color="var(--ink-3)" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ flex: 1 }} />
          <div style={{ marginBottom: 12 }}>
            {/*
              The Ladder speaks in the voice of the Atom you are ABOUT to face,
              which is always the Coach, because a run always starts at Easy. The
              practice bubble still says whatever the difficulty you picked says.
              (The old docked Start CTA is gone: the Ladder's own button is the
              primary action now, and two primary buttons is no primary button.)
            */}
            <AtomBubble
              msg={
                statusMsg ||
                (practiceOpen
                  ? currentLine
                  : "A run starts easy. I'll write the first word that comes to me — same as you, probably.")
              }
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
